import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Points } from '@styles';
import { IS_CLIENT } from '@constants';
import { useTranslations } from '@libs/translations';
import { Picture } from '@components/Picture';

export const Plan = () => {
  const { locale } = useSelector((state) => state.settings);
  const translations = useTranslations();
  const appInterfaceLocale = locale === 'ru' ? locale : 'en';

  const tabs = [
    {
      caption: translations.t('site.home.documentation.tab.plan'),
      alt: translations.t('site.home.documentation.tab.plan-alt'),
      data: {
        '670': [
          `/images/home/documentation/plan/plan-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/plan/plan-${appInterfaceLocale}-1x.webp`,
        ],
        '1x': [
          `/images/home/documentation/plan/plan-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/plan/plan-${appInterfaceLocale}-1x.webp`,
        ],
        '2x': [
          `/images/home/documentation/plan/plan-${appInterfaceLocale}-2x.jpg`,
          `/images/home/documentation/plan/plan-${appInterfaceLocale}-2x.webp`,
        ],
      },
    },
    {
      caption: translations.t('site.home.documentation.tab.plumbers'),
      alt: translations.t('site.home.documentation.tab.plumbers-alt'),
      data: {
        '670': [
          `/images/home/documentation/plumbers/plumbers-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/plumbers/plumbers-${appInterfaceLocale}-1x.webp`,
        ],
        '1x': [
          `/images/home/documentation/plumbers/plumbers-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/plumbers/plumbers-${appInterfaceLocale}-1x.webp`,
        ],
        '2x': [
          `/images/home/documentation/plumbers/plumbers-${appInterfaceLocale}-2x.jpg`,
          `/images/home/documentation/plumbers/plumbers-${appInterfaceLocale}-2x.webp`,
        ],
      },
    },
    {
      caption: translations.t('site.home.documentation.tab.floor'),
      alt: translations.t('site.home.documentation.tab.floor-alt'),
      data: {
        '670': [
          `/images/home/documentation/floor/floor-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/floor/floor-${appInterfaceLocale}-1x.webp`,
        ],
        '1x': [
          `/images/home/documentation/floor/floor-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/floor/floor-${appInterfaceLocale}-1x.webp`,
        ],
        '2x': [
          `/images/home/documentation/floor/floor-${appInterfaceLocale}-2x.jpg`,
          `/images/home/documentation/floor/floor-${appInterfaceLocale}-2x.webp`,
        ],
      },
    },
    {
      caption: translations.t('site.home.documentation.tab.ceiling'),
      alt: translations.t('site.home.documentation.tab.ceiling-alt'),
      data: {
        '670': [
          `/images/home/documentation/ceiling/ceiling-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/ceiling/ceiling-${appInterfaceLocale}-1x.webp`,
        ],
        '1x': [
          `/images/home/documentation/ceiling/ceiling-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/ceiling/ceiling-${appInterfaceLocale}-1x.webp`,
        ],
        '2x': [
          `/images/home/documentation/ceiling/ceiling-${appInterfaceLocale}-2x.jpg`,
          `/images/home/documentation/ceiling/ceiling-${appInterfaceLocale}-2x.webp`,
        ],
      },
    },
    {
      caption: translations.t('site.home.documentation.tab.lighting'),
      alt: translations.t('site.home.documentation.tab.lighting-alt'),
      data: {
        '670': [
          `/images/home/documentation/lighting/lighting-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/lighting/lighting-${appInterfaceLocale}-1x.webp`,
        ],
        '1x': [
          `/images/home/documentation/lighting/lighting-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/lighting/lighting-${appInterfaceLocale}-1x.webp`,
        ],
        '2x': [
          `/images/home/documentation/lighting/lighting-${appInterfaceLocale}-2x.jpg`,
          `/images/home/documentation/lighting/lighting-${appInterfaceLocale}-2x.webp`,
        ],
      },
    },
    {
      caption: translations.t('site.home.documentation.tab.switches'),
      alt: translations.t('site.home.documentation.tab.switches-alt'),
      data: {
        '670': [
          `/images/home/documentation/switches/switches-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/switches/switches-${appInterfaceLocale}-1x.webp`,
        ],
        '1x': [
          `/images/home/documentation/switches/switches-${appInterfaceLocale}-1x.jpg`,
          `/images/home/documentation/switches/switches-${appInterfaceLocale}-1x.webp`,
        ],
        '2x': [
          `/images/home/documentation/switches/switches-${appInterfaceLocale}-2x.jpg`,
          `/images/home/documentation/switches/switches-${appInterfaceLocale}-2x.webp`,
        ],
      },
    },
  ];
  const imageForHeight = tabs?.[0]?.data?.['2x'][0];
  const [index, setIndex] = useState(0);
  const slideTime = 5000;
  const [runAnimated, setRunAnimated] = useState(false);
  let interval = useRef(null);
  let timeout = useRef(null);
  const touchStart = useRef(0);
  const touchEnd = useRef(0);
  const activeTabRef = useRef(null);
  const tabsRef = useRef(null);

  const handleTouchStart = (e) => {
    touchStart.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStart.current - touchEnd.current > 70) {
      if (index === tabs.length) {
        setIndex(0);
      }

      setIndex((prev) => prev + 1);
    }
    if (touchEnd.current - touchStart.current > 70) {
      if (index === 0) {
        setIndex(tabs.length);
      }

      setIndex((prev) => prev - 1);
    }
  };

  const changeSlide = (i = 0) => {
    setIndex(i);
    setRunAnimated(false);

    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }

    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    timeout.current = setTimeout(runAutoSlide, 4000);
  };

  const runAutoSlide = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }

    setRunAnimated(true);
    interval.current = setInterval(() => {
      setIndex((prevIndex) => (prevIndex + 1 >= tabs.length ? 0 : prevIndex + 1));
    }, slideTime);
  };

  useEffect(() => {
    runAutoSlide();

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = null;
      }

      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const tabsElement = tabsRef.current;
    const activeTabElement = activeTabRef.current;

    if (
      !IS_CLIENT ||
      window.innerWidth >= 1024 ||
      !tabsElement ||
      !activeTabElement ||
      !activeTabElement.offsetParent
    ) {
      return;
    }

    const tabsRect = tabsElement.getBoundingClientRect();
    const activeTabRect = activeTabElement.getBoundingClientRect();
    const leftOverflow = activeTabRect.left - tabsRect.left;
    const rightOverflow = activeTabRect.right - tabsRect.right;

    if (leftOverflow < 0) {
      tabsElement.scrollTo({ left: tabsElement.scrollLeft + leftOverflow, behavior: 'smooth' });
    } else if (rightOverflow > 0) {
      tabsElement.scrollTo({ left: tabsElement.scrollLeft + rightOverflow, behavior: 'smooth' });
    }
  }, [index]);

  return (
    <View>
      <ImageWrapper onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
        <ImageForHeight src={imageForHeight} alt="" />

        {tabs.map((tab, i) => (
          <Image key={i} isActive={index === i}>
            <Picture data={tab.data} alt="3D" lazy={false} />
          </Image>
        ))}

        {runAnimated && <PercentLine time={slideTime} />}
      </ImageWrapper>

      <Tabs ref={tabsRef}>
        {tabs.map((tab, i) => (
          <Tab
            key={i}
            ref={i === index ? activeTabRef : null}
            isActive={i === index}
            onClick={() => changeSlide(i)}
            data-gtm={i !== index ? 'tab_click' : null}>
            {tab.caption}
          </Tab>
        ))}
      </Tabs>
    </View>
  );
};

const View = styled.div`
  width: 100%;
`;
const ImageWrapper = styled.div`
  position: relative;
  border: 1px solid ${Colors.light_gray};
  border-radius: 4px;
  box-shadow: -11px 13px 38px 0px rgba(122, 122, 122, 0.25);
  margin-bottom: 38px;
`;

const ImageForHeight = styled.img`
  visibility: hidden;
  opacity: 0;
  width: 100%;
  display: block;
`;
const Image = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  opacity: ${({ isActive }) => (isActive ? 1 : 0)};
  transition: opacity 400ms ease-in-out;
  pointer-events: none;
  overflow: hidden;
`;
const Tabs = styled.div`
  overflow: auto;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  & ::-webkit-scrollbar {
    display: none;
  }

  @media ${Points.tablet_landscape} {
    display: flex;
    flex-wrap: wrap;
  }
`;
const Tab = styled.div`
  font-size: 18px;
  line-height: 1.3;
  padding: 10px;
  background-color: ${({ isActive }) => (isActive ? 'rgba(41, 41, 41, 0.05)' : 'transparent')};
  border-radius: 25px;
  cursor: pointer;
  white-space: nowrap;
  margin-bottom: 10px;
  user-select: none;

  ${({ isActive }) =>
    !isActive &&
    `
      &:hover {
        background-color: rgba(41, 41, 41, 0.03);
      }
    `}

  &:not(&:last-child) {
    margin-right: 10px;
  }
`;

const animate = keyframes`
  from {
    width: 0;
    opacity: 0;
  }
  to {
    width: 100%;
    opacity: 1;
  }
`;

const PercentLine = styled.div`
  width: 100%;
  height: 2px;
  z-index: 2;
  background-color: ${Colors.main_dark};
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  animation: ${animate} ${({ time }) => time}ms ease-out infinite;
`;
